import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  ButtonGroup,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  UnorderedList,
  Spinner,
} from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { isCourseStatusAssignable } from '@duke-hq/shared-constants/course';

type EnrollmentType = 'participant' | 'internal';

type EnrollmentSwitchProps = {
  type: EnrollmentType;
  value: boolean | undefined;
  title: string;
  description: string;
  disableEnable: boolean;
  onUpdate: (type: EnrollmentType, value: boolean) => void;
};

function EnrollmentSwitch({
  type,
  value,
  title,
  description,
  disableEnable,
  onUpdate,
}: EnrollmentSwitchProps) {
  return (
    <HStack justify="space-between">
      <Box>
        <Text>{title}</Text>
        <Text fontSize="sm" color="gray.500">
          {description}
        </Text>
      </Box>
      <ButtonGroup size="sm" isAttached variant="outline">
        <Button
          colorScheme="green"
          onClick={() => onUpdate(type, true)}
          isActive={value === true}
          isDisabled={disableEnable}
          variant={value === true ? 'solid' : 'outline'}
        >
          <Trans>Enable</Trans>
        </Button>

        <Button
          colorScheme="red"
          onClick={() => onUpdate(type, false)}
          isActive={value === false}
          variant={value === false ? 'solid' : 'outline'}
        >
          <Trans>Disable</Trans>
        </Button>
      </ButtonGroup>
    </HStack>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseTitle: string;
  courseStatus: string;
};

export function CourseAssignmentModal({
  isOpen,
  onClose,
  courseId,
  courseTitle,
  courseStatus,
}: Props) {
  const [enrollments, setEnrollments] = useState<{ participant?: boolean; internal?: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const updateCourseAssignment = trpcAdmin.providers.batchUpdateCourseAssignment.useMutation();
  const isAssignable = isCourseStatusAssignable(courseStatus);

  const handleSwitchUpdate = useCallback((type: EnrollmentType, value: boolean) => {
    setEnrollments((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      await updateCourseAssignment.mutateAsync({ courseId, ...enrollments });

      toast({
        title: t`Course assignments updated successfully`,
        status: 'success',
        duration: 5000,
      });

      onClose();
    } catch (error) {
      toast({
        title: t`Failed to update course assignments`,
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [courseId, enrollments, onClose, toast, updateCourseAssignment]);

  const hasChanges = Object.keys(enrollments).length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Assign to all providers</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" variant="left-accent" borderRadius="md">
              <AlertIcon />
              <Box flex="1" overflow="hidden">
                <AlertTitle>
                  <Text
                    fontWeight="bold"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    {courseTitle}
                  </Text>
                </AlertTitle>
                <AlertDescription>
                  <Trans>
                    These changes will affect <strong>all</strong> providers
                  </Trans>
                </AlertDescription>
              </Box>
            </Alert>

            <Text fontWeight="medium" mb={2}>
              <Trans>Enrollment Options</Trans>
            </Text>
            <VStack align="stretch" spacing={4}>
              <EnrollmentSwitch
                type="participant"
                description={t`Allow students to enroll in this course`}
                title={t`Participant Enrollments`}
                value={enrollments.participant}
                disableEnable={!isAssignable}
                onUpdate={handleSwitchUpdate}
              />
              <EnrollmentSwitch
                type="internal"
                description={t`Allow internal enrollments for this course`}
                title={t`Internal Enrollments`}
                value={enrollments.internal}
                disableEnable={!isAssignable}
                onUpdate={handleSwitchUpdate}
              />
            </VStack>

            {hasChanges ? (
              <Alert status="info" variant="left-accent" borderRadius="md" mt={4}>
                <Box>
                  <AlertTitle>
                    <Trans>Pending changes:</Trans>
                  </AlertTitle>
                  <AlertDescription>
                    <UnorderedList spacing={1}>
                      {enrollments.participant !== undefined && (
                        <li>
                          <Trans>
                            {enrollments.participant ? 'Enable' : 'Disable'}{' '}
                            <strong>participant</strong> enrollments for <strong>all</strong>{' '}
                            providers
                          </Trans>
                        </li>
                      )}
                      {enrollments.internal !== undefined && (
                        <li>
                          <Trans>
                            {enrollments.internal ? 'Enable' : 'Disable'} <strong>internal</strong>{' '}
                            enrollments for <strong>all</strong> providers
                          </Trans>
                        </li>
                      )}
                    </UnorderedList>
                  </AlertDescription>
                </Box>
              </Alert>
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} mr={3} isDisabled={isSaving}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isDisabled={!hasChanges || isSaving}
            isLoading={isSaving}
            leftIcon={isSaving ? <Spinner size="sm" /> : undefined}
          >
            <Trans>Apply Changes</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
